// @givmo/assistant-panel — the assistant's SCOPES: one declared list per console of the pages the
// assistant is on, and what it is on each.
//
// ══ THE ICON SHOWS ONLY WHERE A SCOPE IS REGISTERED ════════════════════════════════════════════
//
// Jonathan, 2026-11-01 13:28: the assistant is built out page by page, "the icon appears only on pages
// whose module has had its assistant instructions built, so it's on in production and gains pages over
// time". So a console declares its scopes as DATA — a route pattern, the backend audience the page's
// turns run under (whose instructions and tools live on the backend), the page context each turn
// carries, whether the panel opens by default there — and the console's ONE assistant control renders
// only on a page one of them matches. Everywhere else the cluster carries no assistant markup at all.
//
// Adding a page later is one entry here (plus its audience's instructions and tools on the backend).
// No shell change: the shell mounts the slot and the dock once, and both follow the match.
//
// ══ ONE THREAD PER PERSON, AND THE ONE EXCEPTION ═══════════════════════════════════════════════
//
// `thread: 'person'` is D-EP-40: one persistent thread per person per console audience, whichever page
// it was born on; walking between pages swaps the chip, the prompts and the context a turn carries, and
// nothing else. `thread: 'record'` is the exception, and today only the email designer takes it: the
// thread belongs to the RECORD in view (one conversation per email, its history staying with the
// email), so the scope's context doubles as the thread's match.
//
// ⚠ Still class-blind (D-EP-44): a scope names an audience by its wire string and a page by its
// pattern; nothing here branches on which console or class declared it.

/** What a pattern captured from the address, and the address itself. */
export interface AssistantScopeMatch {
  pathname: string;
  /** Each `$name` segment's value. */
  params: Readonly<Record<string, string>>;
}

/** One page (or family of pages) the assistant is on. */
export interface AssistantScope {
  /** Stable id: the per-session memory of a closed panel is keyed by it, and it is sent as `page`
   *  unless `context` supplies its own. */
  id: string;
  /**
   * The address this scope covers: literal segments, `$name` segments (one segment, captured), and
   * an optional trailing `/*` (this address and anything below it). `'/*'` is every page.
   * The longest literal match wins when several scopes match (`matchAssistantScope`).
   */
  pattern: string;
  /**
   * The `entry_context.surface` the BACKEND recognizes for this page's audience — the thread's
   * identity, and the selector of the instructions and tools its turns run under. A selector among
   * audiences the caller already holds, never a grant (see `AssistantPanelConfig.wireSurface`).
   */
  wireSurface: string;
  /** The surface stamped on each SEND from this page (its register), when it differs from the
   *  thread's. Defaults to `wireSurface`. */
  pageWireSurface?: (match: AssistantScopeMatch) => string;
  /**
   * The page, and the record in view, as the context keys every turn from here carries (stamped on
   * the conversation at creation and on every send). Supply them fresh and complete: the server takes
   * a send's context whole. Absent, the turn carries `{ page: id }`.
   */
  context?: (match: AssistantScopeMatch) => Readonly<Record<string, string>>;
  /** The chip and prompts' surface id for this page (`AssistantSurface.id`); `''` for none. */
  surfaceId?: (match: AssistantScopeMatch) => string;
  /** `'person'`: D-EP-40's one thread. `'record'`: one thread per record, matched on `context`. */
  thread: 'person' | 'record';
  /**
   * Open the panel by default on this page, where it sits beside the page (a wide screen). The person
   * can still close it, and it stays closed on this scope for the rest of their session.
   */
  defaultOpen?: boolean;
  /**
   * The page draws the panel's body itself, through `AssistantPageBody` — the panel keeps its frame
   * (title, close, docking) and the page supplies the conversation (the email designer's, whose turns
   * wait on the page's own hand edits). Absent, the panel draws its own thread.
   */
  pageBody?: boolean;
}

/** A scope and what it matched, or nothing. */
export interface ResolvedAssistantScope {
  scope: AssistantScope;
  match: AssistantScopeMatch;
}

function trimSlashes(path: string): string[] {
  return path.split('/').filter((segment) => segment.length > 0);
}

/** Whether `pattern` covers `pathname`, with its captures; `null` when it does not. */
export function matchScopePattern(
  pattern: string,
  pathname: string,
): { params: Record<string, string>; weight: number } | null {
  const wildcard = pattern.endsWith('/*') || pattern === '*';
  const wanted = trimSlashes(wildcard ? pattern.slice(0, -1) : pattern);
  const got = trimSlashes(pathname);
  if (wildcard ? got.length < wanted.length : got.length !== wanted.length) return null;
  const params: Record<string, string> = {};
  let weight = 0;
  for (let index = 0; index < wanted.length; index += 1) {
    const segment = wanted[index] as string;
    const value = got[index] as string;
    if (segment.startsWith('$')) {
      // A malformed escape (`%E0%A4%A`) is no match rather than a throw: this runs while the shell's
      // top bar renders, and a throw there would take the whole console down with it.
      let decoded: string;
      try {
        decoded = decodeURIComponent(value);
      } catch {
        return null;
      }
      params[segment.slice(1)] = decoded;
      weight += 1;
    } else if (segment === value) {
      weight += 2;
    } else {
      return null;
    }
  }
  // An exact pattern outranks a wildcard of the same depth.
  return { params, weight: weight * 2 + (wildcard ? 0 : 1) };
}

/**
 * The scope the page at `pathname` is in, or `null` when none is registered for it — and then the
 * console shows no assistant control at all. The most specific pattern wins.
 */
export function matchAssistantScope(
  scopes: readonly AssistantScope[],
  pathname: string,
): ResolvedAssistantScope | null {
  let best: ResolvedAssistantScope | null = null;
  let bestWeight = -1;
  for (const scope of scopes) {
    const hit = matchScopePattern(scope.pattern, pathname);
    if (hit !== null && hit.weight > bestWeight) {
      best = { scope, match: { pathname, params: hit.params } };
      bestWeight = hit.weight;
    }
  }
  return best;
}

/** The context every turn from this page carries. */
export function scopeContext(resolved: ResolvedAssistantScope): Readonly<Record<string, string>> {
  return resolved.scope.context?.(resolved.match) ?? { page: resolved.scope.id };
}
